import { Add } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { ExplanationAccordion, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleRecallNoteDto } from 'dtos'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { VehicleRecallNoteParameters, VehicleRecallParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatDateTime, useDebounce } from 'utils'
import RecallNoteAddEditDialog from './RecallNoteAddEditDialog'
import VehicleLockStatusDialog from 'pages/todolist/VehicleLockStatusDialog'

interface RecallNotesListCardProps {
  vehicle: VehicleDto
}

export default function RecallNotesListCard({ vehicle }: RecallNotesListCardProps) {
  const [isGettingRecallNotes, setIsGettingRecallNotes] = useState<boolean>(false)
  const [isSavingRecallNote, setIsSavingRecallNote] = useState<boolean>(false)
  const [vehicleRecallNotes, setVehicleRecallNotes] = useState<VehicleRecallNoteDto[]>([])
  const [recallNote, setRecallNote] = useState<VehicleRecallNoteDto>(new VehicleRecallNoteDto())
  const [recallNotesAddEditDialogOpen, setRecallNotesAddEditDialogOpen] = useState<boolean>(false)
  const [isRecallNoteSaving, setIsRecallNoteSaving] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<VehicleRecallParameters>({
    page: 0,
    pageSize: 5,
    orderBy: 'createdDateTime',
    order: 'desc',
    recallCampaignId: '',
    vehicleRecallStatusId: 0,
    vehicleRecallRepairStatusId: 0
  })

  const onChangeOrderBy = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getVehicleRecallNotes(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle.vehicleGuid, parameters])

  const getVehicleRecallNotes = useDebounce((vehicleGuid: string, parameters: VehicleRecallNoteParameters) => {
    setIsGettingRecallNotes(true)
    controllers
      .getVehicleRecallNotes(vehicleGuid, parameters)
      .then(response => {
        setVehicleRecallNotes(response.value)
        setTotalCount(response.totalCount!)
        console.log(response)
      })
      .finally(() => {
        setIsGettingRecallNotes(false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Card variant='outlined'>
      <RecallNoteAddEditDialog
        initialValues={recallNote}
        isLoading={isRecallNoteSaving}
        onClose={() => {
          setRecallNotesAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsRecallNoteSaving(true)
          controllers
            .createVehicleRecallNote(values, vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Recall Note Saved Successfully!', { variant: 'success' })
              const recallNote = response.value
              setRecallNote(recallNote)
              setRecallNotesAddEditDialogOpen(false)
              getVehicleRecallNotes(vehicle.vehicleGuid, parameters)
            })
            .finally(() => {
              setIsRecallNoteSaving(false)
            })
        }}
        open={recallNotesAddEditDialogOpen}
        vehicleGuid={vehicle.vehicleGuid!}
        parameters={parameters}
      />

      {/* <RecallNoteAddEditDialog
          initialValues={recallNote}
          isLoading={isSavingRecallNote}
          onClose={() => {
            setRecallNotesAddEditDialogOpen(false)
            setRecallNote(new VehicleRecallNoteDto())
          }}
          onSave={(values: VehicleRecallNoteDto) => {
            setIsSavingRecallNote(true)
            controllers
              .createVehicleRecallNote(values, vehicle.vehicleGuid!)
              .then(() => {
                setRecallNotesAddEditDialogOpen(false)
                enqueueSnackbar('Recall Note Saved Successfully!', { variant: 'success' })
                setRecallNote(new VehicleRecallNoteDto())
                getVehicleRecallNotes(vehicle.vehicleGuid, parameters)
              })
              .finally(() => {
                setIsSavingRecallNote(false)
              })
          }}
          open={recallNotesAddEditDialogOpen}
        /> */}

      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm={8}>
              <Typography fontWeight='bold' variant='h5'>
                Recall Notes
              </Typography>
            </Grid>
            <Grid item xs={12} sm='auto'>
              <Button
                endIcon={<Add />}
                onClick={() => {
                  setRecallNote(new VehicleRecallNoteDto())
                  setRecallNotesAddEditDialogOpen(true)
                }}
                variant='text'
              >
                ADD
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingRecallNotes && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingRecallNotes && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='createdDateTime'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Time Stamp
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='recallCampaignId'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Recall Campaign ID
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='createdBy'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Entered By
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='note'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Note
                        </SortableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicleRecallNotes.map(note => (
                        <TableRow>
                          <TableCell>{note.createdDateTime ? formatDateTime(note.createdDateTime) : ''}</TableCell>

                          <TableCell>{note.vehicleRecall?.recallCampaignId}</TableCell>

                          <TableCell>
                            {note.createdBy?.firstName} {note.createdBy?.lastName}
                          </TableCell>

                          <TableCell>{note.note}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          count={totalCount}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              page: 0,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                          page={parameters.page}
                          rowsPerPage={parameters.pageSize}
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
